import React, { Component } from 'react'
import pickBy from 'lodash/pickBy'
import { useSelector } from 'react-redux'
import { NextModal as Modal, PageSelector } from '@components/UI'
import { ClassroomManager, isClient } from '@helpers'
import { Sketch as SK } from '@constants'
import { ShortcutsProvider } from '@contexts'

import Toolbar from './Toolbar'
import Frame from '../Frame'
import Editor from './Editor'
import Cursor from './Cursor'
import OversizeModal from '../Modals/OversizeModal'
import InvalidTypeModal from '../Modals/InvalidTypeModal'

import { EditorContextProvider } from './EditorContext'
import { useClassroomQuery } from '../../hooks'
import { useAuth } from '../../../../hooks'

class Sketch extends Component {
  constructor(props) {
    super(props)
    this.state = {
      tabs: this.getTabs(),
      active: this.getActive()
    }
  }

  componentDidMount() {
    ClassroomManager.observe('frames.sketch.tabs', this.onTabChange)
    ClassroomManager.observe('frames.sketch.active', this.onActiveChange)
  }

  componentWillUnmount() {
    ClassroomManager.unobserve('frames.sketch.tabs', this.onTabChange)
    ClassroomManager.unobserve('frames.sketch.active', this.onActiveChange)
  }

  init = (editor) => {
    ClassroomManager.attachEditor('sketch', editor)
  }

  loadFile = (file) => {
    ClassroomManager.loadFile({ type: 'sketch', id: file })
  }

  onCursorChange = (e) => {
    e.persist()
  }

  onTabChange = () => {
    this.setState({ tabs: this.getTabs() })
  }

  onActiveChange = () => {
    this.setState({ active: this.getActive() })
  }

  getTabs = () => {
    const { classroom, files, profile } = this.props
    const defaultTabs = ClassroomManager.getTabs('sketch')

    if (!isClient(profile.type)) return defaultTabs
    if (classroom.type !== 'group') return defaultTabs

    const filteredTabs = pickBy(defaultTabs, (_, fileId) => {
      const file = files.find((f) => f.id === fileId)

      if (!file) return false

      return file.participants.findIndex((p) => p.id === profile.id) > -1
    })

    return filteredTabs
  }

  getActive = () => {
    const { profile, classroom, files } = this.props
    const defaultActive = ClassroomManager.getActive('sketch')
    const prevValue = this.state?.active
    if (classroom.type !== 'group' || !isClient(profile.type))
      return defaultActive

    const file = files.find((f) => f.id === defaultActive)
    if (!file) return prevValue

    const isParticipant =
      file.participants.findIndex((p) => p.id === profile.id) > -1
    if (!isParticipant) return prevValue

    return defaultActive
  }

  handleChange = (patch, cb) => this.setState(patch, cb)

  onZoomIn = () => {
    const editor = ClassroomManager.getEditor('sketch')
    let zoom = editor.getZoom() + 0.1
    zoom = zoom > 20 ? 20 : zoom
    editor.zoomToPoint({ x: editor.width / 2, y: editor.height / 2 }, zoom)
    editor.fire('position:updated')
  }

  onZoomOut = () => {
    const editor = ClassroomManager.getEditor('sketch')
    let zoom = editor.getZoom() - 0.1
    zoom = zoom < 0.1 ? 0.1 : zoom
    editor.zoomToPoint({ x: editor.width / 2, y: editor.height / 2 }, zoom)
    editor.fire('position:updated')
  }

  renderToolbar = () => {
    return (
      <Toolbar
        close={this.props.close}
        history={{
          undo: () => ClassroomManager.getBinding('sketch').undo(),
          redo: () => ClassroomManager.getBinding('sketch').redo()
        }}
      />
    )
  }

  render() {
    const { state } = this

    return (
      <EditorContextProvider>
        <ShortcutsProvider>
          <Frame
            className='gp-sketch'
            component={{ title: 'Sketch', type: 'sketch' }}
            toolbar={this.renderToolbar()}
            tabDefaultConfig={SK.CONFIG}
            tabs={state.tabs}
            active={state.active}
            progress={state.progress}
            zoomControls={{
              onZoomIn: this.onZoomIn,
              onZoomOut: this.onZoomOut
            }}
          >
            <Editor
              handleChange={this.handleChange}
              active={this.state.active}
              init={this.init}
              loadFile={this.loadFile}
            />
            <Cursor />
            {state.oversize && (
              <OversizeModal
                files={state.oversize}
                close={() => this.setState({ oversize: undefined })}
              />
            )}
            {state.invalid && (
              <InvalidTypeModal
                files={state.invalid}
                close={() => this.setState({ invalid: undefined })}
              />
            )}

            {state.pageSelector && (
              <Modal
                title='Select document page range'
                close={() => this.handleChange({ pageSelector: null })}
              >
                <PageSelector
                  cancel={() => this.handleChange({ pageSelector: null })}
                  select={state.pageSelector.callback}
                  numPages={state.pageSelector.numPages}
                />
              </Modal>
            )}
          </Frame>
        </ShortcutsProvider>
      </EditorContextProvider>
    )
  }
}

const SketchWrapper = (props) => {
  const files = useSelector((state) => state.files.data.map((f) => f.data))
  const { data: classroom } = useClassroomQuery()
  const { profile } = useAuth()

  return (
    <Sketch {...props} files={files} classroom={classroom} profile={profile} />
  )
}

export default SketchWrapper
